import { useCallback, useEffect, useState } from 'react';

type UserPreferences = {
  pageSize?: number;
  theme?: string;
  language?: string;
  [key: string]: any;
};

type StoredPreferences = {
  user_preferences: UserPreferences;
};

function getStorageKey(username: string): string {
  // Ensure username is valid and not null/undefined
  if (!username || username === 'null' || username === 'undefined') {
    console.warn('Invalid username provided to useUserPreferences:', username);
    return 'user_preferences_default';
  }
  return `${username}`;
}

export function useUserPreferences(username: string) {
  const storageKey = getStorageKey(username);

  const readStored = useCallback((): UserPreferences => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed: StoredPreferences = JSON.parse(stored);
        return parsed.user_preferences || {};
      } catch (error) {
        console.warn('Failed to parse stored preferences:', error);
      }
    }
    return {};
  }, [storageKey]);

  const [preferences, setPreferences] = useState<UserPreferences>(readStored);

  // Reload from storage when the user (storageKey) changes. Persistence itself happens in the setters
  // via read-modify-write, NOT via a write-on-change effect: multiple hook instances (e.g., two
  // DataTables mounted at once) each hold their own snapshot, so a blanket "write the whole blob"
  // effect would let the last saver clobber another grid's keys with a stale snapshot.
  useEffect(() => {
    setPreferences(readStored());
  }, [readStored]);

  const setPreference = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      // Re-read the latest blob, merge just this key, and write it back so a concurrent hook
      // instance's keys are preserved instead of overwritten from a stale in-memory snapshot.
      const merged = { ...readStored(), [key]: value };
      localStorage.setItem(storageKey, JSON.stringify({ user_preferences: merged }));
      setPreferences(merged);
    },
    [readStored, storageKey],
  );

  const setAllPreferences = useCallback(
    (newPrefs: UserPreferences) => {
      localStorage.setItem(storageKey, JSON.stringify({ user_preferences: newPrefs }));
      setPreferences({ ...newPrefs });
    },
    [storageKey],
  );

  const logoutUser = () => {
    localStorage.removeItem('userName');
  };

  const getUserName = () => {
    return localStorage.getItem('userName') as string;
  };

  return {
    preferences,
    setPreference,
    setAllPreferences,
    logoutUser,
    getUserName,
  };
}
